import { useEffect, useRef } from 'react';
import { useCallStore } from '../store/useCallStore';
import { useAuthStore } from '../store/useAuthStore';
import { fetchMe } from '../api/auth';
import { applyCompanyDefaults } from '../utils/applyCompanyDefaults';

const BACKEND_URL = (window as Window & { __ENV__?: { BACKEND_URL?: string } }).__ENV__?.BACKEND_URL
  || process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL
  ? BACKEND_URL.replace(/^http/, 'ws') + '/ws'
  : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

interface StatusUpdateMessage {
  type: 'status_update';
  sessionId: string;
  // Realm the entry lives in (companyId, or a status-group id for grouped companies).
  companyId: string | null;
  companyName: string | null;
  // All member companies of the realm — a grouped update must reach every member.
  companyIds?: string[];
  entry: { done: boolean; note: string; updatedBy: string; updatedAt: string };
}

function affectsCompany(companyIds: string[] | undefined, realmId: string | null, myCompanyId: string | null): boolean {
  if (companyIds && companyIds.length > 0) return companyIds.includes(myCompanyId ?? '');
  return realmId === myCompanyId;
}

export function useStatusSocket(onReconnect?: () => void) {
  const updateStatus = useCallStore((s) => s.updateStatus);
  const updateCompanyStatus = useCallStore((s) => s.updateCompanyStatus);
  const bumpSessionVersion = useCallStore((s) => s.bumpSessionVersion);
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    let destroyed = false;
    let isFirstConnect = true;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let currentSocket: WebSocket | null = null;

    function connect() {
      if (destroyed) return;

      const socket = new WebSocket(WS_URL);
      currentSocket = socket;

      socket.onopen = () => {
        useCallStore.getState().setWsConnected(true);
        if (!isFirstConnect) {
          onReconnectRef.current?.();
          // Während der Funkstille verpasste calls_update-Pings ausgleichen:
          // '*' matcht jeden angezeigten Zeitraum.
          useCallStore.getState().signalCallsUpdate('*');
        }
        isFirstConnect = false;
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as { type: string };
          if (msg.type === 'session_update') {
            bumpSessionVersion();
            return;
          }
          if (msg.type === 'calls_update') {
            // Backend-Ticker hat neue Anrufe entdeckt — betroffene Ansichten laden nach.
            const updateMsg = msg as unknown as { period?: string };
            if (updateMsg.period) useCallStore.getState().signalCallsUpdate(updateMsg.period);
            return;
          }
          if (msg.type === 'company_update') {
            // Admin hat die Unternehmens-Konfiguration geändert: /me nachladen und —
            // falls sich die Filter-Vorgaben geändert haben — sofort übernehmen.
            // Kein Re-Login mehr nötig.
            const updateMsg = msg as unknown as { companyId?: string };
            const auth = useAuthStore.getState();
            if (auth.user && auth.user.role !== 'admin' && auth.user.companyId === updateMsg.companyId && auth.token) {
              const before = JSON.stringify(auth.company?.filterConfig ?? null);
              const token = auth.token;
              fetchMe()
                .then(({ user, company }) => {
                  useAuthStore.getState().login(token, user, company);
                  if (JSON.stringify(company?.filterConfig ?? null) !== before) {
                    applyCompanyDefaults(company);
                  }
                })
                .catch(() => undefined);
            }
            return;
          }
          if (msg.type === 'status_update') {
            const statusMsg = msg as unknown as StatusUpdateMessage;
            const currentUser = useAuthStore.getState().user;
            if (currentUser?.role === 'admin') {
              updateCompanyStatus(statusMsg.sessionId, {
                companyId: statusMsg.companyId,
                companyName: statusMsg.companyName,
                done: statusMsg.entry.done,
                note: statusMsg.entry.note,
                updatedBy: statusMsg.entry.updatedBy,
                updatedAt: statusMsg.entry.updatedAt,
              });
            } else if (affectsCompany(statusMsg.companyIds, statusMsg.companyId, currentUser?.companyId ?? null)) {
              updateStatus(statusMsg.sessionId, {
                callbackDone: statusMsg.entry.done,
                callbackNote: statusMsg.entry.note,
                callbackBy: statusMsg.entry.updatedBy,
                callbackAt: statusMsg.entry.updatedAt,
              });
            }
            return;
          }
          if (msg.type === 'status_reset') {
            // Admin cleared a realm's saved statuses — reset the affected calls everywhere.
            const resetMsg = msg as unknown as { companyId: string | null; companyName: string | null; companyIds?: string[]; sessionIds: string[] };
            const currentUser = useAuthStore.getState().user;
            const isAdmin = currentUser?.role === 'admin';
            const matchesCompany = affectsCompany(resetMsg.companyIds, resetMsg.companyId, currentUser?.companyId ?? null);
            if (!isAdmin && !matchesCompany) return;
            for (const sid of resetMsg.sessionIds) {
              if (isAdmin) {
                updateCompanyStatus(sid, {
                  companyId: resetMsg.companyId, companyName: resetMsg.companyName,
                  done: false, note: '', updatedBy: '', updatedAt: '',
                });
              } else {
                updateStatus(sid, { callbackDone: false, callbackNote: '', callbackBy: '', callbackAt: '' });
              }
            }
          }
        } catch {
          // ignore malformed messages
        }
      };

      socket.onclose = () => {
        useCallStore.getState().setWsConnected(false);
        if (!destroyed) {
          reconnectTimeout = setTimeout(connect, 3000);
        }
      };

      // Close the specific socket that errored, not whatever `currentSocket` is at error time
      socket.onerror = () => {
        socket.close();
      };
    }

    connect();

    return () => {
      destroyed = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      // onclose will fire, but `destroyed` prevents it from scheduling a reconnect
      currentSocket?.close();
      currentSocket = null;
    };
  }, [updateStatus, updateCompanyStatus, bumpSessionVersion]);
}
